
import React from "react";
import "./LectureRow.css";

const MAX_VIDEO_SIZE = 2 * 1024 * 1024 * 1024; // 2GB
const MAX_HANDOUT_SIZE = 10 * 1024 * 1024; // 10MB
const ACCEPTED_VIDEO_TYPES = ["video/mp4", "video/quicktime", "video/webm", "video/x-matroska"];
const ACCEPTED_HANDOUT_TYPES = ["application/pdf", "application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"];

function formatFileSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  return (bytes / (1024 * 1024 * 1024)).toFixed(2) + " GB";
}

export default function LectureRow({ rowIndex, lectureNumber, title, onTitleChange, onVideoChange, onHandoutChange, onDelete, lectureDate, onLectureDateChange, topics, selectedTopicId, onTopicSelect }) {
  const [videoFile, setVideoFile] = React.useState(null);
  const [handoutFile, setHandoutFile] = React.useState(null);
  const [videoError, setVideoError] = React.useState("");
  const [handoutError, setHandoutError] = React.useState("");
  const [dateError, setDateError] = React.useState("");

  const handleVideoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      setVideoFile(null);
      setVideoError("");
      onVideoChange(null);
      return;
    }
    if (!ACCEPTED_VIDEO_TYPES.includes(file.type) && !file.name.match(/\.(mp4|mov|webm|mkv)$/i)) {
      setVideoError("Invalid video format. Use MP4, MOV, WebM, or MKV.");
      setVideoFile(null);
      onVideoChange(null);
      return;
    }
    if (file.size > MAX_VIDEO_SIZE) {
      setVideoError("Video exceeds 2GB limit.");
      setVideoFile(null);
      onVideoChange(null);
      return;
    }
    setVideoError("");
    setVideoFile(file);
    onVideoChange(file);
  };

  const handleHandoutChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      setHandoutFile(null);
      setHandoutError("");
      onHandoutChange(null);
      return;
    }
    if (!ACCEPTED_HANDOUT_TYPES.includes(file.type) && !file.name.match(/\.(pdf|doc|docx)$/i)) {
      setHandoutError("Invalid file format. Use PDF, DOC, or DOCX.");
      setHandoutFile(null);
      onHandoutChange(null);
      return;
    }
    if (file.size > MAX_HANDOUT_SIZE) {
      setHandoutError("Handout exceeds 10MB limit.");
      setHandoutFile(null);
      onHandoutChange(null);
      return;
    }
    setHandoutError("");
    setHandoutFile(file);
    onHandoutChange(file);
  };

  const handleDelete = () => {
    setVideoFile(null);
    setHandoutFile(null);
    setVideoError("");
    setHandoutError("");
    setDateError("");
    onDelete();
  };

  const handleDateChange = (e) => {
    const value = e.target.value;
    if (!value) {
      setDateError("");
      if (onLectureDateChange) onLectureDateChange(value);
      return;
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selected = new Date(value);
    if (selected > today) {
      setDateError("Date cannot be in the future");
      if (onLectureDateChange) onLectureDateChange(value);
      return;
    }
    setDateError("");
    if (onLectureDateChange) onLectureDateChange(value);
  };

  return (
    <div className="lecture-row">
      <div className="lecture-row-header">
        <span className="lecture-row-number">Lecture {lectureNumber}</span>
        <button type="button" className="lecture-row-delete" onClick={handleDelete} aria-label="Remove lecture row">
          &times;
        </button>
      </div>
      <div className="lecture-row-fields">
        {/* Topic selection - first */}
        <div className="field-group topic-field-group">
          <label className="field-label">Topic *</label>
          <select
            className="form-select topic-select"
            value={selectedTopicId || ""}
            onChange={(e) => onTopicSelect && onTopicSelect(rowIndex, e.target.value)}
            aria-label="Select topic for this lecture"
          >
            <option value="">-- Select Topic --</option>
            {topics && topics.length > 0 ? (
              topics.map((topic) => (
                <option key={topic.id} value={topic.id}>
                  {topic.name} {topic.language ? `(${topic.language})` : ""}
                </option>
              ))
            ) : (
              <option value="" disabled>No topics available</option>
            )}
          </select>
        </div>
        {/* Lecture Title - second */}
        <div className="field-group">
          <label className="field-label">Title *</label>
          <input
            type="text"
            className="form-input"
            placeholder="Enter lecture title"
            value={title}
            onChange={(e) => onTitleChange(e.target.value)}
          />
        </div>
        {/* Lecture Date - third */}
        <div className="field-group">
          <label className="field-label">Lecture Date *</label>
          <input
            type="date"
            className="form-input lecture-date-input"
            value={lectureDate || ""}
            max={new Date().toISOString().split("T")[0]}
            onChange={handleDateChange}
            aria-label="Lecture date"
          />
          {dateError && <span className="field-error">{dateError}</span>}
        </div>
        {/* Video File - fourth */}
        <div className="field-group">
          <label className="field-label">Video File *</label>
          <input
            type="file"
            className="form-file"
            accept="video/mp4,video/quicktime,video/webm,video/x-matroska,.mp4,.mov,.webm,.mkv"
            onChange={handleVideoChange}
          />
          {videoError && <span className="field-error">{videoError}</span>}
          {videoFile && <span className="field-success">{videoFile.name} ({formatFileSize(videoFile.size)})</span>}
        </div>
        {/* Handout - last */}
        <div className="field-group">
          <label className="field-label">Handout (PDF/DOC)</label>
          <input
            type="file"
            className="form-file"
            accept=".pdf,.doc,.docx"
            onChange={handleHandoutChange}
          />
          {handoutError && <span className="field-error">{handoutError}</span>}
          {handoutFile && <span className="field-success">{handoutFile.name} ({formatFileSize(handoutFile.size)})</span>}
        </div>
      </div>
    </div>
  );
}
